import { useMemo, useState } from 'react';
import { Typography } from '@mui/material';

import { formatIsoDate, isInactiveEpisode } from '../vm/formatters';
import type { HistoryFill, MedicationWorkspaceVM } from '../vm/types';

import { type ActiveFilter, ClinicalCardActions } from './ClinicalCardActions';
import { ClinicalCardFrame } from './ClinicalCardFrame';
import type { ConsentState } from './medicationHistoryConsent';
import { MedicationHistorySection } from './MedicationHistorySection';
import { type MedicationRowActions } from './MedicationRow';
import { MedicationTable } from './MedicationTable';

interface MedicationsCardProps extends MedicationRowActions {
  workspace: MedicationWorkspaceVM;
  /** Which active/inactive filter is selected initially. */
  initialFilter?: ActiveFilter;
  /** Episode ids whose past-prescription drawer starts open. */
  expandedEpisodeIds?: string[];
  /** SureScripts consent state for the pharmacy medication-history section. */
  consentState?: ConsentState;
  /**
   * Pharmacy-claim fills for the history section. Defaults to the design-review
   * examples; containers pass the mapped fills from the unified response.
   */
  historyFills?: HistoryFill[];
  /** SureScripts fills attached to charted medications (rendered in their drawers). */
  attributedFillCount?: number;
  /** Opens the add-medication flow ("+ Add"). */
  onAdd?: () => void;
  /**
   * Records SureScripts consent via the host container. When provided, the
   * history section is fully controlled by `consentState` (no demo simulation).
   */
  onRecordConsent?: () => void;
  /** Notification for the host's analytics — fired after the filter changes. */
  onFilterChanged?: (filter: ActiveFilter) => void;
  /** Org subject language ("patient"/"client") for the consent copy. */
  subject?: string;
  /**
   * SureScripts is only reachable through DoseSpot: hosts pass false when the
   * provider has no e-prescribing setup so the history section isn't noise.
   */
  showHistory?: boolean;
  /** Notification for the host's analytics — history section caret toggled. */
  onHistorySectionToggled?: (expanded: boolean) => void;
  /** 'embedded' hands the chrome and scroll to a dialog host. */
  variant?: 'card' | 'embedded';
  /** Embedded only: renders the header's close button (the dialog's X). */
  onClose?: () => void;
}

/** Vertical cap (~8 rows) before the medication list scrolls internally. */
const TABLE_MAX_HEIGHT = 520;

/**
 * Proposal 0 — AdvancedMD Familiar, shaped to drop into a V1 patient-page card.
 *
 * The card frame (bold "Medications" title + filter + "+ Add") mirrors the
 * shared BaseBlock chrome the live V1 clinical cards use, so patient identity is
 * intentionally omitted — the page header already carries it. One list with a
 * Status column, narrowed by an All / Active / Inactive filter (matching both
 * AdvancedMD and the sibling Diagnoses/Allergies cards) rather than a bespoke
 * tab split. Order/dispense detail hides behind a per-row drawer; the consent-
 * gated pharmacy history sits beneath.
 */
export const MedicationsCard: React.FC<MedicationsCardProps> = ({
  workspace,
  initialFilter = 'all',
  expandedEpisodeIds,
  consentState = 'granted',
  historyFills,
  attributedFillCount,
  onAdd,
  onEdit,
  onManageInDoseSpot,
  onDiscontinue,
  onRemove,
  onHistoryToggled,
  onRecordConsent,
  onFilterChanged,
  subject,
  showHistory = true,
  onHistorySectionToggled,
  variant = 'card',
  onClose,
}) => {
  const [filter, setFilter] = useState<ActiveFilter>(initialFilter);

  const handleFilterChange = (value: ActiveFilter) => {
    setFilter(value);
    onFilterChanged?.(value);
  };

  const episodes = useMemo(() => {
    const active = workspace.episodes.filter((episode) => !isInactiveEpisode(episode));
    const inactive = workspace.episodes.filter(isInactiveEpisode);
    if (filter === 'active') return active;
    if (filter === 'inactive') return inactive;
    return [...active, ...inactive]; // all — current regimen first, then past
  }, [workspace.episodes, filter]);

  const emptyLabel =
    filter === 'inactive' ? 'No past medications on file.' : 'No active medications.';

  const reviewed = workspace.lastVerified
    ? `Last reviewed ${formatIsoDate(workspace.lastVerified.date)} · ${workspace.lastVerified.by}`
    : null;

  return (
    <ClinicalCardFrame
      title="Medications"
      variant={variant}
      onClose={onClose}
      actions={
        <ClinicalCardActions
          filter={filter}
          onFilterChange={handleFilterChange}
          filterLabel="Filter medications"
          onAdd={onAdd}
        />
      }
    >
      {reviewed && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pt: 1 }}
        >
          {reviewed}
        </Typography>
      )}
      {variant === 'embedded' ? (
        // Dialog host: the table owns the flexible scroll area and the
        // history section pins beneath it as a footer strip — reachable
        // without scrolling past the list, scrolling its own content when
        // expanded.
        <>
          <MedicationTable
            episodes={episodes}
            expandedEpisodeIds={expandedEpisodeIds}
            emptyLabel={emptyLabel}
            variant="embedded"
            onEdit={onEdit}
            onManageInDoseSpot={onManageInDoseSpot}
            onDiscontinue={onDiscontinue}
            onRemove={onRemove}
            onHistoryToggled={onHistoryToggled}
          />
          {showHistory && (
            <MedicationHistorySection
              subject={subject}
              initialState={consentState}
              state={onRecordConsent ? consentState : undefined}
              fills={historyFills}
              attributedFillCount={attributedFillCount}
              onRecordConsent={onRecordConsent}
              onSectionToggled={onHistorySectionToggled}
              contentMaxHeight="38vh"
            />
          )}
        </>
      ) : (
        <>
          <MedicationTable
            episodes={episodes}
            expandedEpisodeIds={expandedEpisodeIds}
            emptyLabel={emptyLabel}
            maxHeight={TABLE_MAX_HEIGHT}
            onEdit={onEdit}
            onManageInDoseSpot={onManageInDoseSpot}
            onDiscontinue={onDiscontinue}
            onRemove={onRemove}
            onHistoryToggled={onHistoryToggled}
          />
          {showHistory && (
            <MedicationHistorySection
              subject={subject}
              initialState={consentState}
              state={onRecordConsent ? consentState : undefined}
              fills={historyFills}
              attributedFillCount={attributedFillCount}
              onRecordConsent={onRecordConsent}
              onSectionToggled={onHistorySectionToggled}
            />
          )}
        </>
      )}
    </ClinicalCardFrame>
  );
};
